import React, { useState, useCallback, useEffect } from 'react';
import {
  Pane,
  FileUploader,
  Text,
  Button,
  majorScale,
  Spinner,
  Card,
  Link,
  UnorderedList,
  ListItem,
  Strong,
} from 'evergreen-ui';
import { useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';
import DelimiterSelector from './DelimiterSelector';
import { useCsvImport } from './hooks/useCsvImport';

const requiredFieldsMap = {
  product: ['title', 'format', 'isbn', 'retail_price'],
  order: ['order_group', 'customer_name', 'isbn', 'quantity', 'retail_price'],
  expense: ['amount', 'expense_date'],
  customer: ['company'],
  creator: ['display_name', 'payment_name'],
};

const RequiredFieldsDisplay = ({ entityType }) => {
  const fields = requiredFieldsMap[entityType];

  if (!fields || fields.length === 0) {
    return null;
  }

  const fieldsString = fields.map(field => field.replace(/_/g, ' ')).join(', ');

  return (
    <Pane marginTop={majorScale(2)} marginBottom={majorScale(2)}>
      <Text size={300}>
        <Strong>Required Fields: </Strong>
        {fieldsString}
      </Text>
    </Pane>
  );
};

const FileUpload = ({ entityType, onComplete, onError }) => {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [delimiter, setDelimiter] = useState(',');
  const [encoding, setEncoding] = useState('utf-8');
  const [hasHeader, setHasHeader] = useState(true);
  const [showOptions, setShowOptions] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showCustomerHelp, setShowCustomerHelp] = useState(false);
  const [showExpenseHelp, setShowExpenseHelp] = useState(false);
  const [showCreatorHelp, setShowCreatorHelp] = useState(false);
  const [showProductHelp, setShowProductHelp] = useState(false);
  const { uploadCsvFile } = useCsvImport();
  const currentPublisher = useSelector((state) => state.currentpublisher?.value);

  const currentRequiredFields = requiredFieldsMap[entityType];

  // Use a ref instead of state to track mounted status
  const isMountedRef = React.useRef(true);

  // Set up cleanup function to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Memoize callback functions to prevent re-renders
  const memoizedOnComplete = React.useCallback(onComplete, [onComplete]);
  const memoizedOnError = React.useCallback(onError, [onError]);
  const memoizedUploadCsvFile = React.useCallback(uploadCsvFile, [uploadCsvFile]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'text/csv, application/vnd.ms-excel',
    maxFiles: 1,
  });

  const handleUpload = async () => {
    if (!file) {
      memoizedOnError('Please select a file to upload');
      return;
    }

    if (!currentPublisher || !currentPublisher.id) {
      memoizedOnError('No publisher selected. Please select a publisher first.');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', entityType);
      formData.append('delimiter', delimiter);
      formData.append('encoding', encoding);
      formData.append('has_header', String(hasHeader));
      formData.append('publisher_id', currentPublisher.id);

      console.log('Uploading with publisher ID:', currentPublisher.id);
      console.log('Form data values:', {
        entity_type: entityType,
        delimiter,
        encoding,
        has_header: String(hasHeader),
        publisher_id: currentPublisher.id
      });

      const response = await memoizedUploadCsvFile(formData);
      if (isMountedRef.current) {
        memoizedOnComplete(response);
      }
    } catch (error) {
      if (isMountedRef.current) {
        memoizedOnError(error.message || 'Error uploading file');
      }
    } finally {
      if (isMountedRef.current) {
        setIsUploading(false);
      }
    }
  };

  const toggleOptions = () => {
    setShowOptions(!showOptions);
  };

  const toggleHelp = () => {
    setShowHelp(!showHelp);
  };

  const toggleCustomerHelp = () => {
    setShowCustomerHelp(!showCustomerHelp);
  };

  const toggleExpenseHelp = () => {
    setShowExpenseHelp(!showExpenseHelp);
  };

  const toggleCreatorHelp = () => {
    setShowCreatorHelp(!showCreatorHelp);
  };

  const toggleProductHelp = () => {
    setShowProductHelp(!showProductHelp);
  };

  const downloadTemplate = async () => {
    try {
      const baseUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:8000'
        : '';
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseUrl}/api/csv-import/templates/?template=${entityType}-template.csv`, {
        headers: {
          'Authorization': token ? `Token ${token}` : '',
        },
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to download template');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${entityType}-template.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      memoizedOnError('Error downloading template: ' + error.message);
    }
  };

  return (
    <Pane>
      <Card
        backgroundColor="white"
        elevation={0}
        border="default"
        padding={majorScale(2)}
        marginBottom={majorScale(2)}
      >
        <Pane
          {...getRootProps()}
          border="dashed"
          borderWidth={1}
          borderRadius={4}
          padding={majorScale(3)}
          backgroundColor={isDragActive ? 'tint1' : 'white'}
          cursor="pointer"
          textAlign="center"
        >
          <input {...getInputProps()} />
          {file ? (
            <Text>Selected file: {file.name}</Text>
          ) : isDragActive ? (
            <Text>Drop the CSV file here</Text>
          ) : (
            <Text>
              Drag and drop a CSV file here, or click to select a file
            </Text>
          )}
        </Pane>
      </Card>

      <RequiredFieldsDisplay entityType={entityType} />

      <Pane marginBottom={majorScale(2)} display="flex" alignItems="center">
        {entityType === 'order' && (
          <>
            <Link onClick={toggleHelp} cursor="pointer" marginRight={majorScale(1)}>
              {showHelp ? 'Hide Help' : 'Help'}
            </Link>
            <Text color="muted" marginRight={majorScale(1)}>|</Text>
          </>
        )}

        {entityType === 'customer' && (
          <>
            <Link onClick={toggleCustomerHelp} cursor="pointer" marginRight={majorScale(1)}>
              {showCustomerHelp ? 'Hide Help' : 'Help'}
            </Link>
            <Text color="muted" marginRight={majorScale(1)}>|</Text>
          </>
        )}

        {entityType === 'expense' && (
          <>
            <Link onClick={toggleExpenseHelp} cursor="pointer" marginRight={majorScale(1)}>
              {showExpenseHelp ? 'Hide Help' : 'Help'}
            </Link>
            <Text color="muted" marginRight={majorScale(1)}>|</Text>
          </>
        )}
        {entityType === 'creator' && (
          <>
            <Link onClick={toggleCreatorHelp} cursor="pointer" marginRight={majorScale(1)}>
              {showCreatorHelp ? 'Hide Help' : 'Help'}
            </Link>
            <Text color="muted" marginRight={majorScale(1)}>|</Text>
          </>
        )}
        {entityType === 'product' && (
          <>
            <Link onClick={toggleProductHelp} cursor="pointer" marginRight={majorScale(1)}>
              {showProductHelp ? 'Hide Help' : 'Help'}
            </Link>
            <Text color="muted" marginRight={majorScale(1)}>|</Text>
          </>
        )}

        <Link onClick={downloadTemplate} cursor="pointer" marginRight={majorScale(1)}>
          Download Template
        </Link>

        <Text color="muted" marginRight={majorScale(1)}>|</Text>

        <Link onClick={toggleOptions} cursor="pointer">
          {showOptions ? 'Hide options' : 'Options'}
        </Link>
      </Pane>

      {entityType === 'order' && showHelp && (
        <Card
          backgroundColor="white"
          elevation={0}
          border="default"
          padding={majorScale(2)}
          marginBottom={majorScale(2)}
        >
          <Text size={400} display="block" marginBottom={majorScale(1)}>
            <Strong>Import Notes:</Strong>
          </Text>
          <UnorderedList size={300}>
            <ListItem>
              To download a template, click the Download Template link.
            </ListItem>
            <ListItem>
              If a status, salesperson, shipping method, or payment term does not yet exist, we will auto-create it in settings and continue the import.
            </ListItem>
            <ListItem>
              If no customer exists matching the name provided, we will auto-create the customer (but only the customer's name, not the address).
            </ListItem>
            <ListItem>
              Uploaded orders affect the inventory quantity of the associated products.
            </ListItem>
            <ListItem>
              If an order is marked as 'paid' and has a 'paid_date', we will record the payment.
            </ListItem>
            <ListItem>
              Use the 'order_group' column to group multiple rows (items) into a single order.
            </ListItem>
            <ListItem>
              Duplicate orders are detected based on the 'po_number' field for the same publisher.
            </ListItem>
          </UnorderedList>
        </Card>
      )}

      {entityType === 'customer' && showCustomerHelp && (
        <Card
          backgroundColor="white"
          elevation={0}
          border="default"
          padding={majorScale(2)}
          marginBottom={majorScale(2)}
        >
          <Text size={400} display="block" marginBottom={majorScale(1)}>
            <Strong>Import Notes:</Strong>
          </Text>
          <UnorderedList size={300}>
            <ListItem>
              Please download the customer template.
            </ListItem>
            <ListItem>
              Only the customer name field is required.
            </ListItem>
            <ListItem>
              If a customer class or payment term does not yet exist, we will auto-create it in settings and continue the import.
            </ListItem>
          </UnorderedList>
        </Card>
      )}

      {entityType === 'expense' && showExpenseHelp && (
        <Card
          backgroundColor="white"
          elevation={0}
          border="default"
          padding={majorScale(2)}
          marginBottom={majorScale(2)}
        >
          <Text size={400} display="block" marginBottom={majorScale(1)}>
            <Strong>Expense Import Notes:</Strong>
          </Text>
          <UnorderedList size={300}>
            <ListItem>Download the template for required fields.</ListItem>
            <ListItem>If an expense category or does not yet exist, we will auto-create it in settings and continue the import.</ListItem>
            <ListItem>Expense category names must match exactly those in Settings > Expense Categories, or they will be skipped.</ListItem>
          </UnorderedList>
        </Card>
      )}

      {entityType === 'creator' && showCreatorHelp && (
        <Card
          backgroundColor="white"
          elevation={0}
          border="default"
          padding={majorScale(2)}
          marginBottom={majorScale(2)}
        >
          <Text size={400} display="block" marginBottom={majorScale(1)}>
            <Strong>Creator Import Notes:</Strong>
          </Text>
          <UnorderedList size={300}>
            <ListItem>Download the template for required fields.</ListItem>
            <ListItem>If a creator payment method does not yet exist, we will auto-create it in settings and continue the import.</ListItem>
            <ListItem>Duplicate creators are detected by display_name and email.</ListItem>
          </UnorderedList>
        </Card>
      )}

      {entityType === 'product' && showProductHelp && (
        <Card
          backgroundColor="white"
          elevation={0}
          border="default"
          padding={majorScale(2)}
          marginBottom={majorScale(2)}
        >
          <Text size={400} display="block" marginBottom={majorScale(1)}>
            <Strong>Product Import Notes:</Strong>
          </Text>
          <UnorderedList size={300}>
            <ListItem>Download the template for required fields.</ListItem>
            <ListItem>Duplicates will be checked on the ISBN field.</ListItem>
            <ListItem>If the creator does not yet exist, we will auto-create it in Creators and continue the import.</ListItem>
          </UnorderedList>
        </Card>
      )}

      {showOptions && (
        <DelimiterSelector
          delimiter={delimiter}
          encoding={encoding}
          hasHeader={hasHeader}
          onDelimiterChange={setDelimiter}
          onEncodingChange={setEncoding}
          onHasHeaderChange={setHasHeader}
        />
      )}

      <Pane marginTop={majorScale(3)} display="flex" justifyContent="flex-end">
        <Button
          appearance="primary"
          onClick={handleUpload}
          disabled={!file || isUploading}
          marginRight={majorScale(2)}
        >
          {isUploading ? <Spinner size={16} /> : 'Upload and Continue'}
        </Button>
      </Pane>
    </Pane>
  );
};

export default FileUpload;
